import {
  faBuilding,
  faCoins,
  faTags,
  faTruck,
  faUsers,
  IconDefinition,
} from "@fortawesome/free-solid-svg-icons";
import { useTranslation } from "react-i18next";
import { OrgScreenShell } from "../../../components/OrgShell/OrgScreenShell";
import { SectionNavItem } from "../../../components/OrgShell/SectionNavItem";
import { BadgeChip } from "../../../components/OrgShell/BadgeChip";
import { CustomerUpdateForm_VM } from "../vmHooks/useCustomerFormUpdateViewModel";
import { OrgSectionId } from "../models/organization-update.form";
import { DetailsSection } from "./sections/details-section.component";
import { ShippingSection } from "./sections/shipping-section.component";
import { PricingSection } from "./sections/pricing-section.component";
import { BrandsSection } from "./sections/brands-section.component";
import { UsersContactSection } from "./sections/users-contact-section.component";

interface SectionDefinition {
  id: OrgSectionId;
  /** i18n key for the section nav label — translated at the render site. */
  labelKey: string;
  icon: IconDefinition;
}

const CUSTOMER_SECTIONS: SectionDefinition[] = [
  { id: "details", labelKey: "customerUpdate.sections.details", icon: faBuilding },
  { id: "shipping", labelKey: "customerUpdate.sections.shipping", icon: faTruck },
  { id: "pricing", labelKey: "customerUpdate.sections.pricing", icon: faCoins },
  { id: "brands", labelKey: "customerUpdate.sections.brands", icon: faTags },
  { id: "users", labelKey: "customerUpdate.sections.users", icon: faUsers },
];

const VENDOR_SECTIONS: SectionDefinition[] = [
  { id: "details", labelKey: "customerUpdate.sections.details", icon: faBuilding },
  { id: "shipping", labelKey: "customerUpdate.sections.shipping", icon: faTruck },
  { id: "users", labelKey: "customerUpdate.sections.users", icon: faUsers },
];

export function CustomerUpdateForm(vm: CustomerUpdateForm_VM) {
  const { t } = useTranslation();
  const {
    organization,
    isVendor,
    form,
    setForm,
    fieldErrors,
    showFieldErrors,
    validationSummary,
    isDirty,
    isLoading,
    activeSection,
    onSectionChange,
    onSectionPrefetch,
    onSave,
    onCancel,
    shippingDiffers,
    onShippingDiffersChange,
    pricing,
    brands,
    users,
  } = vm;

  const sections = isVendor ? VENDOR_SECTIONS : CUSTOMER_SECTIONS;
  const excludedCount = form.excludedManufacturerIds.length;

  return (
    <OrgScreenShell
      title={form.name}
      subtitle={
        isVendor
          ? t("vendor")
          : organization?.navNumber
            ? t("customerUpdate.customerWithNav", {
                navNumber: organization.navNumber,
              })
            : t("customer")
      }
      badges={
        <>
          {form.disabled ? (
            <BadgeChip tone="red">{t("customerUpdate.deactivated")}</BadgeChip>
          ) : (
            <BadgeChip tone="green">{t("customerUpdate.active")}</BadgeChip>
          )}
          {!isVendor && form.hidePrices && (
            <BadgeChip tone="orange">
              {t("customerUpdate.pricesHidden")}
            </BadgeChip>
          )}
          {!isVendor && excludedCount > 0 && (
            <BadgeChip tone="gray">
              {t("customerUpdate.brandsExcluded", { count: excludedCount })}
            </BadgeChip>
          )}
          {users.users !== undefined && (
            <BadgeChip tone="gray">
              {t("customerUpdate.usersCount", { count: users.users.length })}
            </BadgeChip>
          )}
        </>
      }
      nav={sections.map((section) => (
        <SectionNavItem
          key={section.id}
          label={t(section.labelKey)}
          icon={section.icon}
          active={activeSection === section.id}
          onClick={() => onSectionChange(section.id)}
          onPrefetch={
            onSectionPrefetch
              ? () => onSectionPrefetch(section.id)
              : undefined
          }
        />
      ))}
      footer={{
        onCancel,
        onSave,
        saveLabel: t("save_changes"),
        // Saving stays clickable — a click surfaces the validation errors.
        error: validationSummary,
        isDirty,
      }}
      isLoading={isLoading}
    >
      {activeSection === "details" && (
        <DetailsSection
          form={form}
          onChange={(patch) => setForm({ ...form, ...patch })}
          navNumber={organization?.navNumber}
          disabled={isVendor}
          isVendor={isVendor}
          required={!isVendor}
          errors={fieldErrors}
          showErrors={showFieldErrors}
        />
      )}
      {activeSection === "shipping" && (
        <ShippingSection
          form={form}
          onChange={(patch) => setForm({ ...form, ...patch })}
          differs={shippingDiffers}
          onDiffersChange={onShippingDiffersChange}
          required={!isVendor}
          errors={fieldErrors}
          showErrors={showFieldErrors}
        />
      )}
      {!isVendor && activeSection === "pricing" && (
        <PricingSection
          commission={pricing.commission}
          commissionOptions={pricing.commissionOptions}
          updateCommission={pricing.updateCommission}
          logoOptions={pricing.logoOptions}
          optionsReady={pricing.optionsReady}
          vatRate={form.vatRate}
          onVatChange={(value) => setForm({ ...form, vatRate: value })}
          hidePrices={form.hidePrices ?? false}
          onHidePricesChange={(next) => setForm({ ...form, hidePrices: next })}
        />
      )}
      {!isVendor && activeSection === "brands" && (
        <BrandsSection
          manufacturers={brands.manufacturers}
          isLoading={brands.isLoading}
          excludedIds={form.excludedManufacturerIds}
          onChange={(excludedManufacturerIds) =>
            setForm({ ...form, excludedManufacturerIds })
          }
        />
      )}
      {activeSection === "users" && (
        <UsersContactSection
          users={users.users}
          isLoading={users.isLoading}
          contact={{
            contactPerson: form.contactPerson,
            contactPhone: form.contactPhone,
            contactEmail: form.contactEmail,
          }}
          onContactChange={(contact) => setForm({ ...form, ...contact })}
          onCreateUser={users.onCreateUser}
          onDeleteUser={users.onDeleteUser}
          onUpdateUser={users.onUpdateUser}
          onToggleUserHidePrices={users.onToggleUserHidePrices}
          isVendor={isVendor}
        />
      )}
    </OrgScreenShell>
  );
}
